/* eslint-disable */
import { useEffect, useState } from 'react';
import { Database, Send, X } from 'lucide-react';
import { useAuth } from '@/Components/Auth/Login/auth';
import { notificationServices } from '@/Services';
import aiPackaging from '@/Services/aiPackaging.services';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';

const RequestOperationDataModal = ({ open, onClose, context = {} }) => {
	const { userConfig = {}, appConfig = {} } = useAuth();
	const userDetails = userConfig?.userDetails || {};

	const [reason, setReason] = useState('');
	const [userName, setUserName] = useState('');
	const [userEmail, setUserEmail] = useState('');
	const [workingArea, setWorkingArea] = useState('');
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!open) return;
		setReason('');
		setUserName(userDetails?.name ?? userDetails?.fullName ?? '');
		setUserEmail(userDetails?.email ?? userDetails?.emailId ?? '');
		setWorkingArea('');
	}, [open, userDetails?.name, userDetails?.email]);

	const handleSubmit = async () => {
		if (!reason?.trim()) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Please tell us why you need this data',
			});
			return;
		}
		if (!workingArea?.trim()) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Please enter your working area',
			});
			return;
		}
		setLoading(true);
		try {
			const response = await aiPackaging.requestOperationData({
				data: {
					reason: reason.trim(),
					department: workingArea?.trim(),
					jsonData: {
						parentTab: context?.parentTab ?? null,
						selectedTab: context?.selectedTab ?? null,
						tableTab: context?.tableTab ?? null,
						operationalDataView: context?.operationalDataView ?? 'process',
						dateRange: context?.dateRange ?? null,
						store: context?.store ?? null,
					},
				},
			});
			if (response?.statusCode === 200) {
				notificationServices.generateNotification({
					type: 'success',
					message: response?.message || 'Your data request has been submitted',
				});
				onClose?.();
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: response?.message || 'Failed to submit request',
				});
			}
		} catch (err) {
			notificationServices.generateNotification({
				type: 'error',
				message: err?.message || 'Failed to submit request',
			});
		} finally {
			setLoading(false);
		}
	};

	if (!open) return null;

	return (
		<div className='fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4'>
			<div className='bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[90vh] overflow-auto'>
				{/* Header */}
				<div className='flex items-center justify-between px-5 py-3 border-b border-gray-200'>
					<div className='flex items-center gap-2'>
						<div className='w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center'>
							<Database size={16} className='text-yellow-700' />
						</div>
						<div>
							<h3 className='font-semibold text-[15px] text-gray-900 leading-tight'>Request Process Data</h3>
							<p className='text-[11px] text-gray-500 leading-tight'>Tell us what you need and we'll prioritise it</p>
						</div>
					</div>
					<button
						type='button'
						onClick={onClose}
						className='text-gray-400 hover:text-gray-700 transition-colors'
						aria-label='Close'
					>
						<X size={18} />
					</button>
				</div>

				{/* Body */}
				<div className='flex flex-col gap-4 px-6 py-5'>
					<div className='flex flex-col gap-1.5'>
						<Label className='text-[13px]'>
							Why do you need this data? <span className='text-red-500'>*</span>
						</Label>
						<textarea
							className='w-full min-h-[120px] rounded-md border border-gray-300 px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 resize-y'
							placeholder='e.g., Need cycle time data for Q2 review'
							value={reason}
							onChange={(e) => setReason(e.target.value)}
						/>
					</div>

					<div className='grid grid-cols-2 gap-3'>
						<div className='flex flex-col gap-1'>
							<Label className='text-[12.5px]'>Your Name</Label>
							<Input
								className='w-full text-[13px]'
								placeholder='Your name'
								value={userName}
								onChange={(e) => setUserName(e.target.value)}
							/>
						</div>
						<div className='flex flex-col gap-1'>
							<Label className='text-[12.5px]'>Email</Label>
							<Input
								className='w-full text-[13px]'
								placeholder='you@company.com'
								type='email'
								value={userEmail}
								onChange={(e) => setUserEmail(e.target.value)}
							/>
						</div>
					</div>

					<div className='flex flex-col gap-1'>
						<Label className='text-[12.5px]'>
							Working Area / Department <span className='text-red-500'>*</span>
						</Label>
						<Input
							className='w-full text-[13px]'
							placeholder='e.g., Operations, Production Planning, Quality'
							value={workingArea}
							onChange={(e) => setWorkingArea(e.target.value)}
						/>
					</div>

					{/* Context preview — read-only, lets user verify what scope they're requesting for */}
					{(context?.parentTab || context?.selectedTab) && (
						<div className='flex flex-col gap-1 mt-1'>
							<Label className='text-[12.5px] text-gray-500'>Requesting data for:</Label>
							<div className='flex flex-wrap gap-1.5'>
								{context?.parentTab && (
									<span className='inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium bg-yellow-50 text-yellow-800 border border-yellow-200'>
										{context.parentTab}
									</span>
								)}
								{context?.selectedTab && (
									<span className='inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium bg-blue-50 text-blue-800 border border-blue-200'>
										{context.selectedTab}
									</span>
								)}
								{context?.tableTab && (
									<span className='inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium bg-gray-100 text-gray-700 border border-gray-300'>
										{context.tableTab}
									</span>
								)}
							</div>
						</div>
					)}
				</div>

				{/* Footer */}
				<div className='flex items-center gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg'>
					<Button
						onClick={onClose}
						variant='outline'
						className='flex-1'
						disabled={loading}
					>
						Cancel
					</Button>
					<Button
						onClick={handleSubmit}
						disabled={loading || !reason?.trim() || !workingArea?.trim()}
						className='flex-1 bg-[#ffb703] hover:bg-yellow-500 text-gray-900 font-semibold flex items-center justify-center gap-1.5'
					>
						{loading ? (
							<>
								<div className='animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-gray-900'></div>
								Sending...
							</>
						) : (
							<>
								<Send size={14} />
								Submit Request
							</>
						)}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default RequestOperationDataModal;
